import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {FieldProp} from '../../types';
import {Text} from './Text';
import {Modal} from '../Modal';
import {Etl} from '../../_services/Etl';
import {FlowFetchResponse} from '../../_services/Flow';
import {ActivityButton} from '../ActivityButton';
import {Alert} from '../Alert';

type PackagingProvisionLookupProps = FieldProp & {};

type Provision = {
    id: number;
    customerNumber: string;
    customerName: string;
    customerPo: string;
    provisionAmount: number;
    remainingAmount: number;
    projectName: string;
}

export const PackagingProvisionLookup: React.FunctionComponent<PackagingProvisionLookupProps> = (props): JSX.Element => {
    const [showLookupModal, setShowLookupModal] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [provisionWorkflows, setProvisionWorkflows] = useState<any>();
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<Provision | undefined>();
    const [searchError, setSearchError] = useState<string>('');

    const inputParams = {
        ...props,
        field: {
            ...props.field,
            gwreadonly_enable: true
        }
    };

    const handleSearch = () => {
        setIsSearching(() => true);
        setSearchError('');

        Etl.getInstance().process('packaging_provision_autocompleter', {q: searchText})
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setProvisionWorkflows(response.json.result);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Workflows');
                }
            })
            .catch((error: any) => {
                setSearchError(error?.message || error || 'Error fetching Workflows');
            })
            .finally(() => {
                setIsSearching(() => false);
            })
    };

    const currencyFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});

    return (
        <>
            {props.displayOnly ?
                <>
                    {props?.value &&
                    <Link to={'/entry/' + props.value}>
                        {props.value}
                    </Link>
                    }
                </>
                :
                <>
                    <div className="d-flex">
                        <div className="flex-grow-1">
                            <Text {...inputParams} type="text"/>
                        </div>
                        <button type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowLookupModal(true)}
                        >Lookup
                        </button>
                    </div>

                    {showLookupModal &&
                    <Modal>
                        <div className="modal-header">
                            <h5 className="modal-title">Packaging Provision Workflow Lookup</h5>
                            <button type="button"
                                    className="close"
                                    tabIndex={-1}
                                    onClick={() => {
                                        setShowLookupModal(false);
                                    }}
                            >×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Enter Packaging Provision WF#, SAP Customer Name or #, Customer PO# or Job Name</p>
                            <div className="d-flex">
                                <input type="text"
                                       className="form-control flex-grow-1"
                                       value={searchText}
                                       onChange={(e) => {
                                           setSearchText(e.target.value);
                                       }}
                                />
                                <ActivityButton type="button"
                                                className="btn btn-secondary"
                                                onClick={() => handleSearch()}
                                                disabled={!searchText}
                                                isActive={isSearching}
                                >Search
                                </ActivityButton>
                            </div>
                            {searchError && <Alert type="danger" message={searchError}/>}
                            {provisionWorkflows && provisionWorkflows.length === 0 &&
                            <p><strong><em>No Provisions found for entered search criteria.</em></strong></p>
                            }
                            {provisionWorkflows && provisionWorkflows.length > 0 &&
                            <>
                                <p className="mt-2 mb-b">Click a provision below to select it. Then click the <em>Use Selected</em> button to attach it to the workflow.</p>
                                <div className="list-group mt-2">
                                    {provisionWorkflows.map((item: Provision) => (
                                        <button key={item.id}
                                                className={[
                                                    'list-group-item list-group-item-action',
                                                    selectedItem?.id === item.id ? ' active' : '',
                                                ].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    setSelectedItem((selectedItem?.id === item.id) ? undefined : item);
                                                }}
                                        >
                                            <div><strong>Request#</strong> {item.id}</div>
                                            <div><strong>SAP Customer:</strong> {item.customerNumber} - {item.customerName}</div>
                                            <div><strong>SAP Customer PO#:</strong> {item.customerPo}</div>
                                            <div><strong>Project:</strong> {item.projectName}</div>
                                            <div><strong>Provision Amount:</strong> {currencyFormatter.format(item.provisionAmount)}</div>
                                            <div><strong>Amount Remaining:</strong> {currencyFormatter.format(item.remainingAmount)}</div>
                                        </button>
                                    ))}
                                </div>
                            </>
                            }
                        </div>
                        <div className="modal-footer">
                            <button type="button"
                                    className="btn btn-primary"
                                    disabled={!selectedItem}
                                    onClick={() => {
                                        props?.onChange && props.onChange(selectedItem?.id, {input_133: selectedItem?.remainingAmount});
                                        setSelectedItem(undefined);
                                        setSearchText('');
                                        setProvisionWorkflows(undefined);
                                        setShowLookupModal(false);
                                    }}
                            >Use Selected
                            </button>
                        </div>
                    </Modal>
                    }
                </>
            }
        </>
    );
};